import { useState } from "react";
import { Modal, Button, Form, Alert, Card, ProgressBar } from "react-bootstrap";
import { observer } from "mobx-react";

function BulkUtmModal({ show, onHide, store }) {
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [name, setName] = useState("");
  const [alert, setAlert] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);

  const handleCreateBulkUtm = async (e) => {
    e.preventDefault();

    if (!utmSource || !name) {
      setAlert({ type: "danger", message: "UTM Source and Campaign Name are required" });
      return;
    }

    try {
      const config = {
        utmSource,
        utmMedium: utmMedium || undefined,
        utmCampaign: utmCampaign || undefined,
        name,
      };

      const response = await store.createBulkUtmConfig(config);
      setBulkResult(response);

      // Reset form on success
      setUtmSource("");
      setUtmMedium("");
      setUtmCampaign("");
      setName("");

      setAlert({
        type: "success",
        message: `Successfully created UTM configuration for ${response.successCount} out of ${response.totalProcessed} deeplinks!`,
      });
    } catch (error) {
      setAlert({
        type: "danger",
        message: error.message || "Failed to create bulk UTM configuration",
      });
    }
  };

  const handleClose = () => {
    setBulkResult(null);
    setAlert(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton className="bg-success text-white">
        <Modal.Title>
          <span className="me-2">🌍</span>
          Create Universal UTM Configuration
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Explanation Section */}
        <div className="mb-4 p-3 bg-light rounded">
          <h6 className="mb-2">🎯 Universal UTM Configuration</h6>
          <p className="mb-0">
            Creates the <strong>same UTM tracking</strong> across ALL {store.totalDeeplinks} deeplinks at once. Perfect
            for Google Ads, email campaigns, or any marketing that could drive traffic to multiple app features.
          </p>
        </div>

        {alert && (
          <Alert variant={alert.type} onClose={() => setAlert(null)} dismissible className="mb-3">
            {alert.message}
          </Alert>
        )}

        {/* Results Display */}
        {bulkResult && (
          <Card className="mb-4">
            <Card.Header className="bg-success text-white">
              <h6 className="mb-0">✅ Bulk Configuration Results</h6>
            </Card.Header>
            <Card.Body>
              <div className="row text-center mb-3">
                <div className="col-4">
                  <div className="border rounded p-3">
                    <h4 className="text-success mb-1">{bulkResult.successCount}</h4>
                    <small className="text-muted">Successfully Configured</small>
                  </div>
                </div>
                <div className="col-4">
                  <div className="border rounded p-3">
                    <h4 className="text-danger mb-1">{bulkResult.errorCount}</h4>
                    <small className="text-muted">Failed</small>
                  </div>
                </div>
                <div className="col-4">
                  <div className="border rounded p-3">
                    <h4 className="text-primary mb-1">{bulkResult.totalProcessed}</h4>
                    <small className="text-muted">Total Processed</small>
                  </div>
                </div>
              </div>

              {bulkResult.successCount > 0 && (
                <>
                  <ProgressBar
                    now={(bulkResult.successCount / bulkResult.totalProcessed) * 100}
                    variant="success"
                    className="mb-3"
                  />
                  <p className="text-center text-success mb-0">
                    <strong>
                      {Math.round((bulkResult.successCount / bulkResult.totalProcessed) * 100)}% Success Rate
                    </strong>
                  </p>
                </>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Configuration Form */}
        <Card>
          <Card.Header>
            <h6 className="mb-0">⚙️ Configuration</h6>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleCreateBulkUtm}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Campaign Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder=""
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
                <Form.Text className="text-muted">A descriptive name that identifies this campaign</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  UTM Source <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., google, facebook, newsletter, instagram"
                  value={utmSource}
                  onChange={(e) => setUtmSource(e.target.value)}
                  required
                />
                <Form.Text className="text-muted">The platform where your campaign will run</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  UTM Medium <span className="text-muted">(Optional)</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., cpc, social, email, stories, video"
                  value={utmMedium}
                  onChange={(e) => setUtmMedium(e.target.value)}
                />
                <Form.Text className="text-muted">The specific medium within the platform (optional)</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  UTM Campaign <span className="text-muted">(Optional)</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., summer-sale, product-launch, holiday-promo"
                  value={utmCampaign}
                  onChange={(e) => setUtmCampaign(e.target.value)}
                />
                <Form.Text className="text-muted">The specific campaign name (optional)</Form.Text>
              </Form.Group>

              <div className="d-flex gap-2">
                <Button
                  type="submit"
                  variant="success"
                  disabled={store.creatingBulkUtm}
                  className="d-flex align-items-center gap-2"
                >
                  {store.creatingBulkUtm ? (
                    <>
                      <span className="spinner-border spinner-border-sm" />
                      Creating for {store.totalDeeplinks} deeplinks...
                    </>
                  ) : (
                    <>🌍 Create for ALL {store.totalDeeplinks} Deeplinks</>
                  )}
                </Button>
                <Button variant="outline-secondary" onClick={handleClose} disabled={store.creatingBulkUtm}>
                  Cancel
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Modal.Body>
    </Modal>
  );
}

export default observer(BulkUtmModal);
